/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useCallback, useEffect, useState } from 'react';

import { Dictionary } from 'lodash';

import {
	BUTTON_TYPES,
	EvButton,
	EvSelect,
	EvSpinner,
	OptionType
} from '@evinced-private/ui-common';

import { AzureApi as azureApi } from '../../api/AzureApi';
import { asyncHandler } from '../../helpers/AsyncHelper';
import { logger } from '../../services/Logger';
import { ScreenshotService } from '../../services/ScreenshotService';
import { IAzureFieldType, IAzureProject } from '../../types/AzureTypes';
import { Report } from '../../types/ReportModel';
import { SiteScannerPopup } from '../common/site-scanner-popup/SiteScannerPopup';

import { AzureHelper } from './Helpers/AzureHelper';
import { IssueFormField } from './issueFields';

import './AzureIssueCreator.scss';

const alertPopupTitle = 'Connect to Azure';
const issueFormTitle = 'Create Issue in Azure';

const fieldsToDisplay = [
	'Work Item',
	'Title',
	'Reason',
	'Priority',
	'Description',
	'Attachment',
	'Assigned To',
	'Area Path',
	'Iteration Path'
];
const fieldsToIgnore = ['Iteration ID', 'Area ID'];

interface IAzureIssueCreatorProps {
	isOpen: boolean;
	closeModal: () => void;
	ticketData: Report;
}

const AzureIssueCreator: FC<IAzureIssueCreatorProps> = ({
	closeModal,
	isOpen,
	ticketData
}: IAzureIssueCreatorProps): JSX.Element => {
	const [isLoading, setLoading] = useState<boolean>(true);
	const [host, setHost] = useState<string>(null);
	const [alertModalOpen, setAlertModalOpen] = useState(false);
	const [projects, setProjects] = useState<IAzureProject[]>([]);
	const [issueTypes, setIssueTypes] = useState<OptionType[]>([]);
	const [selectedProject, setSelectedProject] = useState<OptionType[]>([]);
	const [selectedIssueType, setSelectedIssueType] = useState<OptionType>(null);
	const [fields, setFields] = useState<Dictionary<IAzureFieldType>>(null);
	const [screenshotUrl, setScreenshotUrl] = useState<string>('');

	const updateField = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(newValue: any, field: IAzureFieldType, currentFields?: Dictionary<IAzureFieldType>): void => {
			const updatedField =
				typeof newValue === 'object'
					? { ...field, value: newValue.value }
					: { ...field, value: newValue };

			if (currentFields) {
				setFields({ ...currentFields, [field.referenceName]: updatedField });
			} else {
				setFields({ ...fields, [field.referenceName]: updatedField });
			}
		},
		[fields]
	);

	const updateIssueData = async (newFields: Dictionary<IAzureFieldType>): Promise<void> => {
		try {
			if (ticketData) {
				const boundingBoxes = ticketData.elements.map((element) => element.boundingBox);
				const [screenshotImageData] = await asyncHandler(
					ScreenshotService.getFullPageScreenshotWithIssues({
						pageScreenshotUrl: ticketData.pageScreenshot,
						boundingBoxes
					})
				);

				const updatedFields: Dictionary<IAzureFieldType> = AzureHelper.updateFieldsFromIssueData(
					newFields,
					ticketData
				);

				setFields(updatedFields);

				if (screenshotImageData) {
					setScreenshotUrl(screenshotImageData as string);
				}
			}
		} catch (error) {
			logger.error(
				`Error getting information regarding updateIssueData with error: ${error} with host: ${host}`
			);
		}
	};

	const checkIfLoggedIn = async (): Promise<void> => {
		try {
			const formattedOrganizations: OptionType[] = await azureApi.getOrganizations();

			if (formattedOrganizations.length) {
				setAlertModalOpen(false);

				const organizationUrl = `${AzureHelper.getBaseUrl()}/${formattedOrganizations[0].label}`;
				const formattedProjects: IAzureProject[] = await azureApi.getProjects(organizationUrl);
				const formattedSelectedProject =
					formattedProjects?.length > 1
						? formattedProjects.filter((project) => project.name === formattedProjects[0].label)[0]
						: formattedProjects[0];
				const formattedHost = `${organizationUrl}/${formattedSelectedProject.label}`;

				setProjects(formattedProjects);
				setSelectedProject([formattedSelectedProject]);
				setHost(formattedHost);
			} else {
				setAlertModalOpen(true);
			}
		} catch (error) {
			logger.error(
				`Error getting information regarding fetchData with error: ${error} with host: ${host}`
			);
		}
	};

	const fetchData = async (): Promise<void> => {
		try {
			if (host) {
				const issueTypes: OptionType[] = await azureApi.getProjectTypes(host);
				const formattedProjects: IAzureProject[] = await azureApi.getProjects(host);
				const formattedFields: Dictionary<IAzureFieldType> = await azureApi.getProjectFields(
					host,
					issueTypes[0]?.label
				);

				setFields(formattedFields);
				setIssueTypes(issueTypes);
				setProjects(formattedProjects);
				setSelectedIssueType(issueTypes[0]);
				updateIssueData(formattedFields);
			}
		} catch (error) {
			logger.error(
				`Error getting information regarding fetchData with error: ${error} with host: ${host}`
			);
		} finally {
			setLoading(false);
		}
	};

	const initApi = useCallback(async (): Promise<void> => {
		if (host) {
			try {
				setLoading(true);
				await fetchData();
			} catch (error) {
				logger.error(
					`Error getting information regarding initApi with error: ${error} with host: ${host}`
				);
			}
		}
	}, [host]);

	useEffect(() => {
		checkIfLoggedIn();
	}, []);

	useEffect(() => {
		initApi();
	}, [initApi]);

	const onChangeProjectPicker = async (newProject: OptionType): Promise<void> => {
		const updatedUrl = AzureHelper.replaceProjectName(host, newProject.label);
		try {
			setHost(updatedUrl);
			setLoading(true);
			setSelectedProject([newProject]);
			updateIssueData(fields);
		} catch (error) {
			logger.error(
				`Error getting information regarding onChangeProjectPicker with error: ${error} with host: ${updatedUrl}`
			);
		} finally {
			setLoading(false);
		}
	};

	const onChangeIssueTypePicker = async (
		newIssueType: OptionType,
		previousFields: Dictionary<IAzureFieldType>
	): Promise<void> => {
		setLoading(true);
		try {
			const newFields: Dictionary<IAzureFieldType> = await azureApi.getProjectFields(
				host,
				newIssueType.label
			);
			const overrideFields: Dictionary<IAzureFieldType> = AzureHelper.getOverrideFields(
				newFields,
				previousFields
			);

			setSelectedIssueType(newIssueType);
			updateIssueData(overrideFields);
			setLoading(false);
		} catch (error) {
			logger.error(
				`Error getting information regarding onChangeIssueTypePicker with error: ${error} with host: ${host}`
			);
		} finally {
			setLoading(false);
		}
	};

	const renderProjectPicker = useCallback((): JSX.Element => {
		return (
			<div className="form-element">
				<label className="new-form-label" htmlFor="project">
					Project <span className="require-sign">*</span>
				</label>
				<div className="form-input">
					<EvSelect
						id="project"
						options={projects}
						onChange={onChangeProjectPicker}
						isSearchable
						value={selectedProject}
						placeholder="Select project"
					/>
				</div>
			</div>
		);
	}, [projects, selectedProject]);

	const renderIssueTypePicker = useCallback((): JSX.Element => {
		return (
			<div className="form-element">
				<label className="new-form-label" htmlFor="issuetype">
					Issue Type <span className="require-sign">*</span>
				</label>
				<div className="form-input">
					<EvSelect
						id="issuetype"
						options={issueTypes}
						onChange={(value) => onChangeIssueTypePicker(value, fields)}
						isSearchable
						value={[selectedIssueType]}
						placeholder="Select project"
					/>
				</div>
			</div>
		);
	}, [selectedProject, selectedIssueType, issueTypes, fields]);

	// publish button should be disabled as long as one of the required fields is not full
	const isPublishButtonDisabled = (): boolean => {
		if (fields) {
			const isDisabled = Object.keys(fields).some((fieldKey) => {
				const field = fields[fieldKey];

				// so the button should be disabled in such case
				if (field.alwaysRequired && !field.value && !fieldsToIgnore.includes(field.name)) {
					return true;
				}
				return field.alwaysRequired ? !fields[fieldKey] : false;
			});
			return isDisabled;
		}
		return true;
	};

	const createIssue = async (): Promise<void> => {
		try {
			setLoading(true);
			const shouldCloseModal = await azureApi.createIssue(
				host,
				selectedIssueType.label,
				screenshotUrl,
				fields
			);

			if (shouldCloseModal) {
				closeModal();
			}
		} catch (error) {
			logger.error(
				`Error getting information regarding createIssue with error: ${error} with host: ${host}`
			);
		} finally {
			setLoading(false);
		}
	};

	const renderActionButtons = (): JSX.Element => {
		return (
			<>
				<EvButton
					className="cancel-button"
					onClick={closeModal}
					type={BUTTON_TYPES.SECONDARY}
					title="Cancel"
				>
					Cancel
				</EvButton>
				<EvButton
					disabled={isPublishButtonDisabled()}
					type={BUTTON_TYPES.PRIMARY}
					title="Create"
					onClick={createIssue}
				>
					Create
				</EvButton>
			</>
		);
	};

	const renderDynamicForm = useCallback((): JSX.Element => {
		return (
			<>
				{Object.entries(fields).map(([, field]) => {
					return fieldsToDisplay.includes(field.name) ||
						(field.alwaysRequired && !fieldsToIgnore.includes(field.name)) ? (
						<IssueFormField
							key={field.name}
							field={field}
							onChange={(value) => updateField(value, field)}
						/>
					) : null;
				})}
			</>
		);
	}, [fields]);

	const renderAttachment = (): JSX.Element => {
		return screenshotUrl ? (
			<div className="form-element" key="system.Attachment">
				<label className="new-form-label">Attachment</label>
				<div className="form-input">
					<div className="azure-issue-attachments">
						<div className="attachments-screenshot">
							<img alt="screenshot" src={screenshotUrl} />
						</div>
					</div>
				</div>
			</div>
		) : null;
	};

	const buttons = [
		{
			title: 'OK',
			onClick: () => {
				logger.info('Error popup closed on OK click');
				closeModal();
			}
		}
	];

	if (!host && alertModalOpen) {
		return (
			<SiteScannerPopup
				title={alertPopupTitle}
				buttons={buttons}
				isControlled
				isCentered
				isOpen={alertModalOpen}
				className="error-popup"
				onClose={closeModal}
				popupWidth="100%"
			>
				<div className="alert-popup">Azure is not connected</div>
			</SiteScannerPopup>
		);
	}

	return (
		<SiteScannerPopup
			title={issueFormTitle}
			isOpen={isOpen}
			popupWidth="100%"
			className="azure-popup"
			isBgTransparent
			isControlled
			onClose={closeModal}
		>
			{!isLoading && isOpen ? (
				<div className="azure-issue-creator">
					<div className="azure-issue-content">
						<div className="azure-issue-form">
							<div className="azure-form-fields ">
								{renderProjectPicker()}
								{renderIssueTypePicker()}
								{renderDynamicForm()}
								{renderAttachment()}
							</div>
						</div>
					</div>
					<div className="azure-issue-action-buttons">{renderActionButtons()}</div>
				</div>
			) : (
				<div className="azure-issue-creator">
					<div className="spinner-wrapper">
						<EvSpinner />
					</div>
				</div>
			)}
		</SiteScannerPopup>
	);
};

export { AzureIssueCreator };
